import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    output,
    Signal,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { Canvas, FabricObject, TPointerEventInfo } from 'fabric';
import { Hotspot, IDisplaySlide, ImageQuality } from '@pcp/core/interfaces';
import { ITransferDynamicContentSlide, swapImageQualityInUrl } from '@pcp/streaming';
import { PCP_LOGGER } from '@pcp/logging';
import { DrawingToolName } from 'shared/classroom/toolbar/core';
import {
    type DrawStyle,
    exportObjectsToBlob,
    LOGICAL_CANVAS_HEIGHT,
    LOGICAL_CANVAS_WIDTH,
} from 'shared/fabric-drawing/tools';
import { FabricCanvasFitDirective } from './fabric-canvas-fit.directive';
import { DebugConfigService } from 'shared/util-runtime-config';
import { ScrollableViewport, ViewportSize } from './scrollable-viewport';
import { SlideBackground } from './slide-background';
import { BandedObject, CoordinateSpace, PointerContext, SlideCapability, SlideHostContext } from './slide-capability';
import { CapabilityCallbacks, resolveRecipe, SlideRecipe } from './slide-recipe';
import { SlideDrawingTools } from './slide-drawing-tools';
import { SlideCanvasBindingPort, SlideCanvasSyncHandle } from './slide-canvas-binding-port';
import { HIGHLIGHT_CURSOR_FIELD, HighlightCursorController } from './highlight-cursor';
import { FABRIC_CANVAS_FACTORY } from './fabric-canvas-factory';
import { slideCanvasPixelRatio } from './slide-canvas-pixel-ratio';
import { SLIDE_CANVAS_TEXTBOARD_ICON_CONFIG } from './textboard-icon-config';

/** Result of {@link SlideCanvasRendererComponent.exportToBlob}: the master image + geometry for variant derivation. */
export interface SlideCanvasExport {
    /** Encoded master image (requested WebP; read `master.type` for the encoder's actual output). */
    master: Blob;
    /**
     * Master pixel size — INFORMATIONAL only (mirrors fabric's floored output). Consumers
     * derive authoritative dimensions from the decoded `master` itself.
     */
    width: number;
    height: number;
    /** Visible window in master pixels (pan viewport for panorama/bookpage, full master otherwise). */
    viewport: { x: number; y: number; width: number; height: number };
    /**
     * `true` (panorama): `low`/`med`/`high` all use the master unchanged — one native-resolution
     * size, mirroring the content pipeline's panorama variants. `false`: derive the 16:9 ladder.
     */
    uniformLadder: boolean;
}

/**
 * SlideCanvasRendererComponent — the DUMB fabric slide renderer (`canvas-renderer-composition`,
 * Decision C15). Everything it knows arrives as signal inputs; every intent leaves as an output;
 * its single collaborator is the {@link SlideCanvasBindingPort} that binds the renderer-owned
 * `fabric.Canvas` to a drawing-sync backend. It has NO classroom knowledge: no facade, no
 * layer concept, no store — this lib is `type:ui`, so a `data-access` import is a lint error.
 *
 * What it owns (absorbed from the former `CanvasSlideHost` plain class — now component-native,
 * so there is no `Injector` threading and no manual `EffectRef` bookkeeping):
 * - The `fabric.Canvas` (created via {@link FABRIC_CANVAS_FACTORY} at the logical 1280×720 size,
 *   CSS-fitted by `FabricCanvasFitDirective`) and the attach/detach lifecycle of the sync handle.
 * - **Registry composition** (Decisions C8/C12/C6): {@link resolveRecipe} maps the slide to one
 *   background strategy + capability list; the renderer reconciles capabilities (reuse-or-recreate
 *   by kind), never branching on `slide.type` itself.
 * - **Ordered pointer claim-chain**: one set of canvas pointer handlers; `mouse:down` is offered to
 *   capabilities in priority order — first `claims()` wins — with grab-to-pan as lowest-priority
 *   fallback (pan offsets leave via `viewBoxChange` per input event and are not applied locally —
 *   the container decides what comes back down and how often it is persisted; see `onPan` there).
 * - **Host-owned z-band re-assertion**: capabilities contribute banded objects; the renderer
 *   re-asserts `content → drawings → overlay` after every render and after a full sync reconcile
 *   (via the handle's `onAfterReconcile` hook), surviving the binding's `canvas.clear()`.
 * - The drawing-tools subsystem ({@link SlideDrawingTools}), client-side export, and the
 *   Ctrl+Shift+D debug-overlay hotkey (unified `config.debug`, dev builds only).
 *
 * Init order: the constructor declares `computed`s and `ready`-guarded `effect`s; `afterNextRender`
 * creates the canvas, attaches the port, builds tools/capability context, then flips `ready` — the
 * guarded effects then perform the initial slide/viewport/tool passes that used to run imperatively
 * in the host constructor. Teardown runs on `DestroyRef`: handle `detach()` strictly BEFORE
 * `canvas.dispose()` (the binding unhooks its canvas listeners in `detach`).
 */
@Component({
    selector: 'pcp-slide-canvas-renderer',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FabricCanvasFitDirective],
    // The fit-host fills the available area and centers the canvas; FabricCanvasFitDirective
    // (ResizeObserver + setDimensions cssOnly) keeps the canvas contain-fitted to it at 16:9
    // — never stretched, even when the host is wider/taller than 16:9 (e.g. the beamer/
    // projector case, or the teacher workspace when its preview cell is width-capped for
    // teacher-text readability). The canvas fills the cell when it is a tight 16:9 and
    // letterboxes within it otherwise.
    template: `
        <div class="fit-host" [pcpFabricCanvasFit]="canvasInstance()" [fabricCanvasFitAspectRatio]="aspectRatio">
            <canvas #canvasEl></canvas>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
                height: 100%;
                overflow: hidden;
            }
            .fit-host {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        `,
    ],
})
export class SlideCanvasRendererComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly createCanvas = inject(FABRIC_CANVAS_FACTORY);
    private readonly log = inject(PCP_LOGGER).createScoped('slide-canvas-renderer');
    private readonly textboardIconConfig = inject(SLIDE_CANVAS_TEXTBOARD_ICON_CONFIG);
    private readonly debugConfig = inject(DebugConfigService);

    /** The slide to render (its `imageResource` is applied as the LOCAL background). */
    readonly slide = input.required<IDisplaySlide>();
    /**
     * This client's preferred image quality (`canvas-image-quality`). Applied to the LOCAL
     * background by URL convention (`swapImageQualityInUrl`); undefined → the synced URL is used
     * as transferred. Also derives the backing-store multiplier, read ONCE at canvas creation —
     * a later quality change updates the displayed variant immediately, the buffer only at the
     * next mount (design D2).
     */
    readonly imageQuality = input<ImageQuality | undefined>(undefined);
    /**
     * Binds the renderer-owned canvas to the drawing-sync backend. Attached ONCE in
     * `afterNextRender` — pass a reference-stable object, not a per-CD-cycle literal.
     */
    readonly bindingPort = input.required<SlideCanvasBindingPort>();
    /** Active drawing tool (null = default/pan mode). */
    readonly activeTool = input<DrawingToolName | null>(null);
    /**
     * Kill-switch for follower apps: hotspot hit-testing and activation are suppressed
     * (parity with the SVG `pointer-events:none` wrapper gate). Followers still render
     * whatever the synced state shows.
     */
    readonly disableElementUsage = input(false);
    /** Stroke style for the drawing tools. */
    readonly drawStyle = input<DrawStyle>({ color: '#000000', width: 4, brushStyle: 'solid' });
    /** Bookpage paging: whether a previous / next page exists. */
    readonly hasPreviousPage = input(false);
    readonly hasNextPage = input(false);
    /** Synced scroll offset (viewBox string) for panorama/bookpage slides; drives the viewport transform. */
    readonly viewBox = input<string | undefined>(undefined);
    /** Whether this client may draw/pan/deactivate (writer role). Gates tools, pan, and paging affordances. */
    readonly isWriter = input(false);
    /** Synced set of deactivated hotspot indices — projected as dimmed + skipped in hit-testing. */
    readonly deactivatedHotspots = input<ReadonlySet<number>>(new Set<number>());
    /** Synced dynamic-content list — projected by the graphic-overlay / textboard-content capabilities. */
    readonly dynamicContent = input<ITransferDynamicContentSlide[]>([]);
    /** Synced textboard page (1-based) — projected by the textboard-content capability, never re-emitted. */
    readonly textboardPage = input<number | undefined>(undefined);
    /**
     * Scroll-video frame override (`canvas-scroll-video-slides`): the host resolves the CURRENT
     * frame's image URL (already SAS-signed; frames carry no quality variants) and passes it down.
     * Non-null → applied as the local background instead of the synced `imageResource`.
     */
    readonly scrollVideoFrameUrl = input<string | null>(null);

    /** A hotspot region was activated (mouse:down inside it). */
    readonly hotspotClick = output<Hotspot>();
    /** Bookpage paging: the writer activated the previous / next corner affordance. */
    readonly previousPage = output<void>();
    readonly nextPage = output<void>();
    /** The writer activated a deactivatable hotspot at this index — persist the deactivation. */
    readonly deactivateHotspot = output<number>();
    /** A pan gesture produced a new viewBox — persist it (the renderer does not apply it locally). */
    readonly viewBoxChange = output<string>();
    /** Textboard page intent (button click / writer append-jump) — persist it via the debounced write path. */
    readonly textboardPageChange = output<number>();
    /** Scroll-video scrub intent: the writer wheeled one frame step (the host owns index math + sync). */
    readonly scrollVideoScrub = output<'left' | 'right'>();

    /** Logical aspect ratio (16:9) — drives the fit directive. */
    protected readonly aspectRatio = LOGICAL_CANVAS_WIDTH / LOGICAL_CANVAS_HEIGHT;
    /** The fabric canvas, exposed to the template so `FabricCanvasFitDirective` can size it. */
    protected readonly canvasInstance = signal<Canvas | undefined>(undefined);

    private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');

    /** Flips true once `afterNextRender` built the canvas + subsystems; gates every effect below. */
    private readonly ready = signal(false);

    // --- Canvas-lifetime state (born in afterNextRender, torn down in dispose) ---

    private canvas?: Canvas;
    private handle?: SlideCanvasSyncHandle;
    private drawingTools?: SlideDrawingTools;
    private ctx?: SlideHostContext;
    private capabilityCallbacks?: CapabilityCallbacks;

    /**
     * Right-mouse highlight cursor (`canvas-highlight-cursor`): a global, tool-independent laser
     * pointer streamed over the ephemeral awareness channel. The controller owns the local + remote
     * arrows; `highlightActive` tracks the writer's in-progress right-drag; the cleanup removes the
     * native pointer listeners attached to the fabric upper-canvas element.
     */
    private highlightCursor?: HighlightCursorController;
    private highlightActive = false;
    private removeHighlightListeners?: () => void;

    /** Active capabilities in priority order, parallel to {@link capabilityKinds}. */
    private capabilities: SlideCapability[] = [];
    private capabilityKinds: string[] = [];
    /** Banded objects the renderer has added to the canvas (for re-assert add/remove bookkeeping). */
    private readonly contributed = new Set<FabricObject>();

    /** Grab-to-pan state (the lowest-priority pointer claim). */
    private panning = false;
    private panStartCoord = 0;
    private panStartOffset = 0;

    /** Host-cell size feeding the coordinate space. Fixed-logical today; `canvas-bookpage-slides-viewport` measures it. */
    private readonly hostSize: ViewportSize = { width: LOGICAL_CANVAS_WIDTH, height: LOGICAL_CANVAS_HEIGHT };

    /**
     * The resolved recipe for the current slide — a `computed` over `slide()`, NOT a cached field
     * ("derive, don't cache"). Reading it inside a reactive context tracks `slide()`, so downstream
     * derivations ({@link currentBackground}, {@link scrollVp}) re-run automatically.
     */
    private readonly recipe: Signal<SlideRecipe> = computed(() =>
        resolveRecipe(this.slide(), message => this.log.warn(message)),
    );
    /** Background strategy for the current slide (derived from {@link recipe}; may be a degraded fallback). */
    private readonly currentBackground: Signal<SlideBackground> = computed(() => this.recipe().background);
    /** Scroll model for the current slide, or null when it doesn't scroll (derived from {@link currentBackground}). */
    private readonly scrollVp: Signal<ScrollableViewport | null> = computed(() =>
        this.currentBackground().scrollModel(this.hostSize),
    );
    /** Renderer-owned coordinate space (Decision C9): viewport + content-scale; recomputes on slide change. */
    private readonly coordinateSpace: Signal<CoordinateSpace> = computed(() =>
        this.currentBackground().coordinateSpace(this.hostSize),
    );
    /**
     * The LOCAL background image URL: the single synced `imageResource`, swapped to this client's
     * preferred quality variant by URL convention (`canvas-image-quality`). No quality (or a URL
     * without the quality segment — bookpages, empty whiteboard) → the URL as transferred.
     * A scroll-video's current frame ({@link scrollVideoFrameUrl}) overrides both: the host
     * resolves it per frame index, and it carries no quality segment to swap.
     */
    private readonly backgroundUrl: Signal<string | null> = computed(() => {
        const frameUrl = this.scrollVideoFrameUrl();
        if (frameUrl) return frameUrl;
        const url = this.slide().imageResource ?? null;
        const quality = this.imageQuality();
        return url && quality ? swapImageQualityInUrl(url, quality) : url;
    });
    /**
     * Dynamic-content items with each graphic's `imageResource` swapped to this client's preferred
     * variant — the same convention as {@link backgroundUrl}. The Y.Doc item carries ONE URL
     * (written by the teacher at ITS quality, SAS included); each client derives its variant
     * locally. Items without an image (textboard paragraphs) pass through untouched. Capabilities
     * and the reconcile effect read THIS list, so a quality change reloads graphic overlays.
     */
    private readonly localDynamicContent: Signal<ITransferDynamicContentSlide[]> = computed(() => {
        const quality = this.imageQuality();
        const items = this.dynamicContent();
        if (!quality) return items;
        return items.map(item =>
            item.imageResource ? { ...item, imageResource: swapImageQualityInUrl(item.imageResource, quality) } : item,
        );
    });

    constructor() {
        // Component-native reactivity (Decision C15, absorbing C13's host-owned effects): each
        // effect is ONE concern and starts with the `ready` gate — pre-ready runs read only the
        // `ready` signal, so `input.required` is never touched before it is set, and the real
        // dependencies are tracked on the first post-ready run. Keeping them minimal is what stops
        // a method's incidental reads (e.g. the pan cursor's `activeTool`/`isWriter`) from
        // re-running an unrelated effect.
        // The LOCAL background is its OWN effect — declared BEFORE onSlideChange so a slide change
        // still applies the background before capabilities reconcile (as the former inline call
        // did), while a quality-only change re-applies just the background and never reconciles
        // capabilities (geometry is variant-independent).
        effect(() => {
            if (!this.ready()) return;
            this.applyBackground();
        });
        effect(() => {
            if (!this.ready()) return;
            this.onSlideChange();
        });
        effect(() => {
            if (!this.ready()) return;
            this.syncViewport();
        });
        effect(() => {
            if (!this.ready()) return;
            this.hasPreviousPage();
            this.hasNextPage();
            this.requestRender();
        });
        // Synced hotspot-deactivation set changed (Decision C14) → repaint so the projection (debug
        // dimming) reflects it; hit-testing reads the signal live so it needs no render.
        effect(() => {
            if (!this.ready()) return;
            this.deactivatedHotspots();
            this.requestRender();
        });
        // Synced dynamic-content list changed (append / reset / slide-change clear) → reconcile the
        // content capability's objects NOW, outside the render cycle. Reconciling here (not only in
        // the after:render pass) is what makes a reset/clear repaint immediately: a removal done
        // inside after:render can't reliably request its own follow-up render, so it would otherwise
        // stay on screen until the next unrelated interaction. `untracked`: reassertBands calls into
        // the capabilities' contributeObjects, whose incidental signal reads (isWriter, paging
        // availability, textboard page) must not leak into THIS effect's dependencies — the explicit
        // trigger read is the only tracked dependency. Tracks the QUALITY-SWAPPED list, so a
        // quality change also reconciles (graphic overlays reload their variant).
        effect(() => {
            if (!this.ready()) return;
            this.localDynamicContent();
            untracked(() => this.reassertBands());
        });
        // Synced textboard page changed (local intent echo or remote turn) → re-reconcile so the
        // textboard capability contributes the new page's paragraphs; it never re-emits the change.
        effect(() => {
            if (!this.ready()) return;
            this.textboardPage();
            untracked(() => this.reassertBands());
        });
        effect(() => {
            if (!this.ready()) return;
            this.drawingTools?.applyTool();
        });
        effect(() => {
            if (!this.ready()) return;
            this.drawingTools?.refreshStyle();
        });
        // Pan cursor is its OWN effect: `refreshPanCursor` reads `activeTool`/`isWriter`/`scrollVp`,
        // so folding it into `onSlideChange` would leak those deps into the slide-change effect and
        // rebuild capabilities on every tool switch. Isolated here, onSlideChange tracks only `slide()`.
        effect(() => {
            if (!this.ready()) return;
            this.refreshPanCursor();
        });
        // Unified debug overlay (`config.debug`) toggled → repaint so the hotspot/textboard debug
        // overlays appear/disappear immediately. The app-wide Ctrl+Shift+D handler lives in
        // provideDebugConfig() (shared/util-runtime-config); this component only reacts.
        effect(() => {
            if (!this.ready()) return;
            this.debugConfig.enabled();
            this.requestRender();
        });

        afterNextRender(() => {
            const canvas = this.createCanvas(this.canvasRef().nativeElement, {
                width: LOGICAL_CANVAS_WIDTH,
                height: LOGICAL_CANVAS_HEIGHT,
                // Owned by SlideDrawingTools.applyTool, which re-derives it from isWriter + the
                // active tool on its first (post-ready) run and on every later change.
                selection: false,
                backgroundColor: '#ffffff',
                preserveObjectStacking: true,
                // Quality-derived backing store (`canvas-image-quality`, design D2). Read ONCE —
                // afterNextRender is not a reactive context, so a later quality change resizes
                // the buffer only at the next mount (the variant URL updates immediately).
                pixelRatio: slideCanvasPixelRatio(this.imageQuality(), window.devicePixelRatio),
            });
            this.canvas = canvas;
            this.handle = this.bindingPort().attach(canvas);
            this.drawingTools = new SlideDrawingTools(canvas, {
                isWriter: this.isWriter,
                activeTool: this.activeTool,
                drawStyle: this.drawStyle,
            });

            // Capabilities receive the reactive inputs directly (C13/C15): the interaction signals
            // and the coordinate-space signal — the renderer's own inputs, not facade reads.
            this.ctx = {
                canvas,
                interaction: {
                    isWriter: this.isWriter,
                    disableElementUsage: this.disableElementUsage,
                    activeTool: this.activeTool,
                },
                coordinateSpace: this.coordinateSpace,
            };
            this.capabilityCallbacks = {
                hotspots: {
                    onActivate: h => this.hotspotClick.emit(h),
                    // Passed DOWN: the synced deactivation set (Decision C14). Intent UP: only a writer
                    // emits — every client then dims whatever deactivation the writer persisted.
                    deactivated: this.deactivatedHotspots,
                    onDeactivate: index => {
                        if (this.isWriter()) this.deactivateHotspot.emit(index);
                    },
                },
                paging: {
                    onPage: side => (side === 'prev' ? this.previousPage.emit() : this.nextPage.emit()),
                    hasPreviousPage: this.hasPreviousPage,
                    hasNextPage: this.hasNextPage,
                },
                // Passed DOWN: the synced dynamic-content list (graphic overlays / textboard),
                // with graphic image URLs swapped to this client's quality (`canvas-image-quality`).
                dynamicContent: this.localDynamicContent,
                // Textboard: synced page DOWN, page intent UP; the brand config expands into
                // concrete styles/icons/offsets in the capability factory (design D5), not here.
                textboard: {
                    page: this.textboardPage,
                    onPageIntent: page => this.textboardPageChange.emit(page),
                    config: this.textboardIconConfig,
                },
                // Scroll-video: scrub intent UP only — the resolved frame comes back DOWN as the
                // `scrollVideoFrameUrl` background override, not through the capability.
                scrollVideo: {
                    onScrub: direction => this.scrollVideoScrub.emit(direction),
                },
            };

            // A full sync reconcile clears the canvas and re-adds drawings on top; re-assert the
            // capability bands (content below drawings) within that pass so the first paint is correct.
            this.handle.onAfterReconcile(() => this.reassertBands());

            // Highlight cursor (`canvas-highlight-cursor`): render remote peers' cursors on EVERY
            // client (writers + followers) via the ephemeral awareness relay; the writer's own
            // right-drag gesture is wired separately below. The binding's awareness listener skips
            // this client's own id, so a writer never double-renders its own cursor.
            this.highlightCursor = new HighlightCursorController(canvas);
            this.handle.registerEphemeral(
                HIGHLIGHT_CURSOR_FIELD,
                (payload, clientId) => this.highlightCursor?.applyRemote(payload, clientId) ?? false,
            );

            this.setupPointerHandlers(canvas);
            this.setupHighlightCursorGesture(canvas);
            // Hand the canvas to FabricCanvasFitDirective, which keeps it contain-fitted to the host
            // on resize (ResizeObserver + setDimensions cssOnly).
            this.canvasInstance.set(canvas);
            // Flip the gate: the effects above now perform the initial slide/viewport/tool passes.
            this.ready.set(true);
        });

        this.destroyRef.onDestroy(() => this.dispose());
    }

    // ── Reactive method bodies (each self-tracks the signals it reads) ─────────────────

    /**
     * New slide (or same-slide content reset): re-resolve the recipe, re-apply the background,
     * reconcile capabilities, and push the new slide to each. Reads ONLY `slide()` (via the recipe) —
     * the pan cursor and viewport transform are separate effects, so a pan or a tool switch never
     * rebuilds capabilities.
     */
    private onSlideChange(): void {
        const canvas = this.canvas;
        const ctx = this.ctx;
        const cb = this.capabilityCallbacks;
        if (!canvas || !ctx || !cb) return;

        // Highlight cursors belong to the slide being shown — drop any lingering arrow(s) and end an
        // in-progress writer gesture so nothing carries into the new slide's scene space.
        this.endHighlightCursor();
        this.highlightCursor?.dispose();

        const recipe = this.recipe();

        // Reconcile to the recipe's capabilities, in priority order, reusing persisting instances.
        const current = new Map(this.capabilityKinds.map((kind, i) => [kind, this.capabilities[i]] as const));
        const next: SlideCapability[] = [];
        const nextKinds: string[] = [];
        for (const spec of recipe.capabilities) {
            const existing = current.get(spec.kind);
            next.push(existing ?? spec.create(ctx, cb));
            nextKinds.push(spec.kind);
            current.delete(spec.kind);
        }
        current.forEach(cap => cap.dispose()); // no longer wanted
        this.capabilities = next;
        this.capabilityKinds = nextKinds;

        const slide = this.slide();
        for (const cap of this.capabilities) cap.onSlideChange(slide);
        canvas.requestRenderAll();
    }

    /**
     * Drive the fabric viewport transform from the synced viewBox, then broadcast the scroll
     * offset so capabilities can keep viewport-pinned affordances in place (bookpage folds).
     * Non-scrolling slides reset to identity. The transform survives object reconciliation
     * (the binding's `canvas.clear()` does NOT reset `viewportTransform`).
     */
    private syncViewport(): void {
        const canvas = this.canvas;
        if (!canvas) return;
        const sv = this.scrollVp();
        if (!sv) {
            canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
            this.broadcastViewport(0);
            return;
        }
        const offset = sv.parseOffset(this.viewBox());
        const [tx, ty] = sv.direction === 'Vertical' ? [0, -offset] : [-offset, 0];
        canvas.setViewportTransform([1, 0, 0, 1, tx, ty]);
        this.broadcastViewport(sv.direction === 'Vertical' ? offset : 0);
    }

    private broadcastViewport(offsetY: number): void {
        for (const cap of this.capabilities) cap.onViewportChange?.(offsetY);
    }

    /** Request a render so the after-render band re-assert picks up changed inputs (e.g. paging availability). */
    private requestRender(): void {
        this.canvas?.requestRenderAll();
    }

    private dispose(): void {
        // Highlight cursor: clear the field so peers drop this client's arrow, then unbind + dispose.
        this.endHighlightCursor();
        this.removeHighlightListeners?.();
        this.removeHighlightListeners = undefined;
        this.highlightCursor?.dispose();
        this.highlightCursor = undefined;
        this.drawingTools?.dispose();
        for (const cap of this.capabilities) cap.dispose();
        this.capabilities = [];
        this.capabilityKinds = [];
        this.contributed.clear();
        // Detach strictly BEFORE canvas.dispose(): detach unhooks the binding's canvas listeners.
        this.handle?.onAfterReconcile(undefined);
        this.handle?.detach();
        void this.canvas?.dispose();
    }

    // ── Pointer dispatch (single owner: claim-chain + pan fallback, Decision C12) ────────

    private setupPointerHandlers(canvas: Canvas): void {
        canvas.on('mouse:down', o => this.handlePointerDown(canvas, o));
        canvas.on('mouse:move', o => this.handlePointerMove(canvas, o));
        canvas.on('mouse:up', () => {
            this.panning = false;
        });
        canvas.on('mouse:wheel', opt => this.handleWheel(opt));
        canvas.on('after:render', () => this.handleAfterRender());
    }

    private pointerContext(canvas: Canvas, o: TPointerEventInfo): PointerContext {
        return { scenePoint: canvas.getScenePoint(o.e), viewportPoint: canvas.getViewportPoint(o.e) };
    }

    // ── Highlight cursor: right-mouse laser pointer (`canvas-highlight-cursor`, tool-independent) ──

    /**
     * Wire the always-on right-mouse highlight gesture with NATIVE listeners on the fabric upper
     * canvas, NOT fabric's `mouse:*` events: fabric suppresses non-primary-button events (only fires
     * them when `fireRightClick` is set) AND swallows `mouse:move` entirely while `isDrawingMode`
     * (pencil) is active — so fabric events can't back a gesture that must work under every tool.
     * Native listeners also match the legacy SVG `MousePointer` precedent. Scene coordinates come
     * from {@link highlightScenePoint} (NOT `canvas.getScenePoint`, which mis-scales the pan — see
     * that method). Fabric already early-returns for the right button on mousedown, so no pencil
     * stroke / selection / pan starts.
     */
    private setupHighlightCursorGesture(canvas: Canvas): void {
        const el = canvas.upperCanvasEl as HTMLCanvasElement | undefined;
        if (!el) return; // no interactive element (e.g. a headless/mock canvas) → nothing to bind

        const onContextMenu = (e: MouseEvent) => e.preventDefault(); // never show the browser menu on right-drag
        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 2 || !this.isWriter()) return; // right button, writers only
            e.preventDefault();
            this.highlightActive = true;
            this.moveHighlightCursor(canvas, e);
        };
        const onMouseMove = (e: MouseEvent) => {
            if (!this.highlightActive) return;
            // Right button no longer held (released off-canvas, then re-entered) → end the gesture.
            if ((e.buttons & 2) === 0) {
                this.endHighlightCursor();
                return;
            }
            this.moveHighlightCursor(canvas, e);
        };
        const onMouseUp = (e: MouseEvent) => {
            if (e.button === 2) this.endHighlightCursor();
        };
        const onMouseLeave = () => this.endHighlightCursor();

        el.addEventListener('contextmenu', onContextMenu);
        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('mousemove', onMouseMove);
        el.addEventListener('mouseup', onMouseUp);
        el.addEventListener('mouseleave', onMouseLeave);
        this.removeHighlightListeners = () => {
            el.removeEventListener('contextmenu', onContextMenu);
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('mousemove', onMouseMove);
            el.removeEventListener('mouseup', onMouseUp);
            el.removeEventListener('mouseleave', onMouseLeave);
        };
    }

    /** Render the writer's own arrow immediately and publish the scene point + color to peers. */
    private moveHighlightCursor(canvas: Canvas, e: MouseEvent): void {
        const p = this.highlightScenePoint(canvas, e);
        const color = this.drawStyle().color;
        this.highlightCursor?.showLocal(p.x, p.y, color);
        this.handle?.publishEphemeral(HIGHLIGHT_CURSOR_FIELD, { x: p.x, y: p.y, color });
    }

    /**
     * Pointer → logical scene point, panorama-pan-safe. NOT `canvas.getScenePoint`: fabric (6.9)
     * inverts the `viewportTransform` BEFORE applying the CSS-fit scale (`getPointer`), so under our
     * `cssOnly` contain-fit (display size ≠ logical 1280×720) a panned viewport mis-scales the pan
     * translation → the arrow drifts from the cursor proportionally to the scroll offset. Instead take
     * the correctly-scaled viewport point and invert the viewport transform ourselves, in logical
     * space. `syncViewport` only ever writes a pure translation `[1,0,0,1,tx,ty]`, so subtracting the
     * translation is the exact inverse; for a non-scrolling slide (identity transform) this equals the
     * viewport point (what `getScenePoint` would return there).
     */
    private highlightScenePoint(canvas: Canvas, e: MouseEvent): { x: number; y: number } {
        const vp = canvas.getViewportPoint(e);
        const vpt = canvas.viewportTransform;
        return { x: vp.x - vpt[4], y: vp.y - vpt[5] };
    }

    /** End the gesture: remove the local arrow and clear the ephemeral field so peers drop it too. */
    private endHighlightCursor(): void {
        if (!this.highlightActive) return;
        this.highlightActive = false;
        this.highlightCursor?.clearLocal();
        this.handle?.publishEphemeral(HIGHLIGHT_CURSOR_FIELD, null);
    }

    private handlePointerDown(canvas: Canvas, o: TPointerEventInfo): void {
        const pointer = this.pointerContext(canvas, o);
        // Claim-chain: first capability (in priority order) to claim the point handles it.
        for (const cap of this.capabilities) {
            if (cap.claims?.(pointer)) {
                cap.onPointerDown?.(pointer);
                return;
            }
        }
        // Fallback: grab-to-pan (lowest priority), only in the default tool mode on a scrollable slide.
        if (!this.panEnabled()) return;
        const sv = this.scrollVp();
        if (!sv) return;
        this.panStartCoord = this.axisCoord(canvas, o, sv);
        this.panStartOffset = sv.parseOffset(this.viewBox());
        this.panning = true;
        canvas.setCursor('grabbing');
    }

    private handlePointerMove(canvas: Canvas, o: TPointerEventInfo): void {
        const pointer = this.pointerContext(canvas, o);
        for (const cap of this.capabilities) cap.onPointerMove?.(pointer);
        if (!this.panning) return;
        const sv = this.scrollVp();
        if (!sv) return;
        // Drag right/down → offset decreases (grab-and-drag the image).
        this.emitOffset(sv, sv.applyDrag(this.panStartOffset, this.panStartCoord, this.axisCoord(canvas, o, sv)));
    }

    /**
     * Wheel dispatch: offered to the capabilities first (claim-chain, like pointers) — a
     * scroll-video claims it to scrub frames — then falls back to wheel-to-pan, which is always
     * active on a scrollable slide for writers, regardless of the active tool. The claim order is
     * what makes a panorama scroll-video scrub on wheel and pan only by drag (design D6).
     */
    private handleWheel(opt: { e: Event }): void {
        const e = opt.e as WheelEvent;
        for (const cap of this.capabilities) {
            if (cap.onWheel?.(e.deltaY, e.deltaMode)) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
        }
        const sv = this.scrollVp();
        if (!sv || !this.isWriter()) return;
        e.preventDefault();
        e.stopPropagation();
        this.emitOffset(sv, sv.applyWheel(sv.parseOffset(this.viewBox()), e.deltaY, e.deltaMode));
    }

    /**
     * Host-owned z-band re-assertion (Decision C6/C8): after each render, ensure the canvas contains
     * exactly the capabilities' contributed objects, in band order, surviving the binding's
     * `canvas.clear()`. Then let capabilities paint non-object overlays (hotspot debug).
     */
    private handleAfterRender(): void {
        this.reassertBands();
        for (const cap of this.capabilities) cap.onAfterRender?.();
    }

    /**
     * Reconcile canvas membership to the capabilities' contributed objects and enforce the z-bands
     * relative to the drawings the binding owns (which sit BETWEEN content and overlay). Called both
     * from the after-render pass and — for flicker-free ordering after a full sync reconcile — from
     * the handle's `afterReconcile` hook (Decision C8). `canvas.add()` appends to the top, so explicit
     * ordering is required: content is sent to the back (below drawings), overlay brought to the front.
     * If membership changed, request another render so freshly-added objects paint.
     */
    private reassertBands(): void {
        const canvas = this.canvas;
        if (!canvas) return;
        const desired: BandedObject[] = [];
        for (const cap of this.capabilities) {
            if (cap.contributeObjects) desired.push(...cap.contributeObjects());
        }
        const desiredObjects = new Set(desired.map(d => d.object));
        let changed = false;
        for (const obj of this.contributed) {
            if (!desiredObjects.has(obj)) {
                canvas.remove(obj);
                this.contributed.delete(obj);
                changed = true;
            }
        }
        for (const entry of desired) {
            if (!canvas.contains(entry.object)) {
                canvas.add(entry.object);
                changed = true;
            }
            this.contributed.add(entry.object);
        }
        const content = desired.filter(d => d.band === 'content').map(d => d.object);
        const overlay = desired.filter(d => d.band === 'overlay').map(d => d.object);
        if (content.length > 0 || overlay.length > 0) {
            // Steady-state fast path: fabric's sendObjectToBack/bringObjectToFront no-op ONLY when the
            // object is already at the exact end index — any actual move fires _onStackOrderChanged →
            // requestRenderAll (renderOnAddRemove), which re-schedules a frame from within after:render.
            // Re-running the reorder loops on an already-ordered stack permutes-and-restores it whenever
            // a band has ≥2 objects (the reverse loop cycles the array), so without this check every
            // render schedules the next one — a self-sustaining ~60fps loop on e.g. any textboard.
            const objects = canvas.getObjects();
            const inBandOrder =
                content.every((o, i) => objects[i] === o) &&
                overlay.every((o, i) => objects[objects.length - overlay.length + i] === o);
            if (!inBandOrder) {
                // Enforce band z-order around the drawings (which sit between). Content → back, in
                // REVERSE so append order is preserved (first-appended ends lowest, later graphics
                // stack above earlier). Overlay → front, in append order.
                for (let i = content.length - 1; i >= 0; i--) canvas.sendObjectToBack(content[i]);
                for (const o of overlay) canvas.bringObjectToFront(o);
            }
        }
        if (changed) canvas.requestRenderAll();
    }

    private axisCoord(canvas: Canvas, o: TPointerEventInfo, sv: ScrollableViewport): number {
        const vp = canvas.getViewportPoint(o.e);
        return sv.direction === 'Vertical' ? vp.y : vp.x;
    }

    private panEnabled(): boolean {
        return !!this.scrollVp() && this.isWriter() && this.activeTool() === null;
    }

    private refreshPanCursor(): void {
        if (this.canvas) this.canvas.defaultCursor = this.panEnabled() ? 'grab' : 'default';
    }

    /** Emit a pan offset as the new viewBox (the model clamps it). The synced value flows back via `viewBox`. */
    private emitOffset(sv: ScrollableViewport, offset: number): void {
        this.viewBoxChange.emit(sv.formatViewBox(offset));
    }

    /** Apply this client's background variant ({@link backgroundUrl}) with the strategy's fit. */
    private applyBackground(): void {
        this.handle?.setBackgroundLocal(this.backgroundUrl(), this.currentBackground().fit);
    }

    // ── Export ──────────────────────────────────────────────────────────────────────────

    /** Whether the canvas currently holds any exportable content objects (drawings, dynamic/textboard content). */
    hasExportableContent(): boolean {
        return (this.handle?.snapshotObjects().length ?? 0) > 0;
    }

    /**
     * Export the current slide — the chosen background variant composited with the drawing objects —
     * as a WebP master Blob, rebuilt on an OFFSCREEN canvas from the object model
     * (`canvas-slide-favorites` D1/D3). Uses the HIGH variant so the export is maximum quality
     * regardless of which variant this client displays; the scale and ladder shape come from the
     * background strategy's {@link SlideBackground.exportPolicy} (16:9 → 3840×2160 with a derived
     * ladder; panorama → native content resolution with a uniform ladder). Null if not attached.
     */
    async exportToBlob(): Promise<SlideCanvasExport | null> {
        const handle = this.handle;
        if (!handle) return null;
        const slide = this.slide();
        const objects = handle.snapshotObjects();
        const background = this.currentBackground();
        const { width, height } = background.exportSize(this.hostSize);
        const { multiplier, uniformLadder } = background.exportPolicy();
        // Encoding (WebP @ DEFAULT_EXPORT_QUALITY) comes from the shared export defaults, so the
        // master and the client-derived variants can never diverge (`canvas-slide-favorites` D3).
        const master = await exportObjectsToBlob(objects, {
            width,
            height,
            backgroundUrl: this.resolveExportBackgroundUrl(slide),
            multiplier,
        });
        return {
            master,
            // Truncate like the canvas element's width/height setters do in fabric's toCanvasElement.
            width: Math.floor(width * multiplier),
            height: Math.floor(height * multiplier),
            viewport: this.exportViewport(width, height, multiplier),
            uniformLadder,
        };
    }

    /**
     * The visible window in master pixels: the current pan offset for scrollable slides
     * (panorama/bookpage — offsets live in logical px, the same space as `exportSize`), the
     * full master otherwise. Thumbnails render from this region (D3: a full panorama squeezed
     * to 16:9 would be unusable as a thumbnail).
     */
    private exportViewport(
        exportWidth: number,
        exportHeight: number,
        multiplier: number,
    ): SlideCanvasExport['viewport'] {
        const sv = this.scrollVp();
        if (!sv) return { x: 0, y: 0, width: exportWidth * multiplier, height: exportHeight * multiplier };
        const offset = sv.parseOffset(this.viewBox());
        const [x, y] = sv.direction === 'Vertical' ? [0, offset] : [offset, 0];
        return {
            x: x * multiplier,
            y: y * multiplier,
            width: LOGICAL_CANVAS_WIDTH * multiplier,
            height: LOGICAL_CANVAS_HEIGHT * multiplier,
        };
    }

    /**
     * Export always uses the HIGH variant, derived from the synced URL by convention — regardless
     * of the variant this client displays. (`slide.imageVariants` is stripped from the Y.Doc
     * transfer shape and is undefined at display time, so it cannot be consulted here.)
     */
    private resolveExportBackgroundUrl(slide: IDisplaySlide): string | null {
        const url = slide.imageResource ?? null;
        return url ? swapImageQualityInUrl(url, ImageQuality.HIGH) : url;
    }
}
